import { apiRequest } from '@/lib/api-client';

export type CampaignObjective = 'awareness' | 'traffic' | 'engagement' | 'conversions' | 'sales';
export type CampaignStatus =
  | 'draft'
  | 'pending_approval'
  | 'active'
  | 'paused'
  | 'rejected'
  | 'completed';

export interface CampaignPlan {
  budgetAmount: number;
  budgetCurrency: string;
  cadence: string;
  audience: string;
  adCopy: string;
  headlines: string[];
  creativeIdeas: string[];
  notes: string;
  source: 'llm' | 'heuristic';
  plannedAt: string | null;
}

export interface CampaignOptimization {
  at: string;
  kind: string;
  rationale: string;
  change: Record<string, unknown>;
}

export interface Campaign {
  id: string;
  name: string;
  objective: CampaignObjective;
  status: CampaignStatus;
  requestedBudget: number;
  productIds: string[];
  plan: CampaignPlan;
  pendingOptimization: { kind: string; rationale: string } | null;
  optimizations: CampaignOptimization[];
  meta: {
    syncState: 'local' | 'synced' | 'not_connected' | 'error';
    campaignId: string | null;
    adSetId: string | null;
    syncError: string | null;
  };
  launchedAt: string | null;
  createdAt: string;
}

export interface CampaignPerformance {
  reach: number;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  ctr: number;
  roas: number;
  snapshotCount: number;
}

export interface CreateCampaignBody {
  name: string;
  objective: CampaignObjective;
  productIds: string[];
  requestedBudget: number;
}

const base = (orgId: string, projectId: string) =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/campaigns`;

export const campaignsApi = {
  list: (orgId: string, projectId: string) => apiRequest<Campaign[]>(base(orgId, projectId)),
  create: (orgId: string, projectId: string, body: CreateCampaignBody) =>
    apiRequest<Campaign>(base(orgId, projectId), { method: 'POST', body }),
  plan: (orgId: string, projectId: string, id: string) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/${id}/plan`, { method: 'POST' }),
  pause: (orgId: string, projectId: string, id: string) =>
    apiRequest<Campaign>(`${base(orgId, projectId)}/${id}/pause`, { method: 'POST' }),
  resume: (orgId: string, projectId: string, id: string) =>
    apiRequest<Campaign>(`${base(orgId, projectId)}/${id}/resume`, { method: 'POST' }),
  remove: (orgId: string, projectId: string, id: string) =>
    apiRequest<{ deleted: boolean }>(`${base(orgId, projectId)}/${id}`, { method: 'DELETE' }),
  monitor: (orgId: string, projectId: string) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/monitor`, { method: 'POST' }),
  performance: (orgId: string, projectId: string, id: string) =>
    apiRequest<CampaignPerformance>(`${base(orgId, projectId)}/${id}/performance`),
  optimize: (orgId: string, projectId: string, id: string) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/${id}/optimize`, { method: 'POST' }),
};
